import { ref, watch, computed, onMounted } from "@vue/composition-api";
import store from "@/store";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ability from "@/libs/acl/ability";

export default function useBankRecordsList() {
  // 使用 toast
  const toast = useToast();

  const refBankRecordsListTable = ref(null);
  const bankRecordsList = ref([]);
  const totalRow = ref(0);
  const currentPage = ref(1);
  const perPage = ref(10);
  const searchQuery = ref("");
  const sortBy = ref("date"); // 預設按日期排序
  const isSortDirDesc = ref(true); // 預設降序排列（最新日期在前）
  const filter_payment_method_id = ref(null);
  const filter_start_date = ref(null);
  const filter_end_date = ref(null);
  const filter_min_amount = ref(null);
  const filter_max_amount = ref(null);
  const filter_is_savings = ref(null);
  const filter_transaction_type = ref(null);

  const dataMeta = computed(() => {
    const localItemsCount = refBankRecordsListTable.value
      ? refBankRecordsListTable.value.localItems.length
      : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalRow.value,
    };
  });

  const refetchData = () => {
    refBankRecordsListTable.value.refresh();
  };

  watch([
    currentPage,
    perPage,
    searchQuery,
    filter_payment_method_id,
    filter_start_date,
    filter_end_date,
    filter_min_amount,
    filter_max_amount,
    filter_is_savings,
    filter_transaction_type
  ], () => {
    refetchData();
  });

  const perPageOptions = [10, 25, 50, 100];

  // 表格列定義 - 新的銀行進支記錄格式
  const tableColumns = [
    {
      key: "date",
      label: "日期",
      sortable: true,
      formatter: (value) => formatDate(value)
    },
    {
      key: "transaction_detail",
      label: "進支詳情",
      sortable: false
    },
    {
      key: "payment_method",
      label: "進支方式",
      sortable: false
    },
    {
      key: "deposit_amount",
      label: "存入",
      sortable: true,
      formatter: (value) => value > 0 ? `HK$${Number(value).toFixed(2)}` : '',
      // class: 'text-right'
    },
    {
      key: "withdrawal_amount",
      label: "支出",
      sortable: true,
      formatter: (value) => value > 0 ? `HK$${Number(value).toFixed(2)}` : '',
      // class: 'text-right'
    },
    {
      key: "is_savings",
      label: "儲蓄/往來",
      sortable: false
    },
    {
      key: "remark",
      label: "備注",
      sortable: false
    },
    {
      key: "actions",
      label: "刪除",
      sortable: false
    }
  ];

  // 移除複雜的資料轉換邏輯，後端已統一處理

  // 存儲全部展開的數據，用於匯出
  const allExpandedItems = ref([]);

  const fetchBankRecords = (ctx, callback) => {
    store
      .dispatch("bankRecords/fetchBankRecords", {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        payment_method_id: filter_payment_method_id.value,
        start_date: filter_start_date.value,
        end_date: filter_end_date.value,
        min_amount: filter_min_amount.value,
        max_amount: filter_max_amount.value,
        is_savings: filter_is_savings.value,
        transaction_type: filter_transaction_type.value,
      })
      .then((response) => {
        const { bank_records, total } = response.data;

        // 後端已處理完整的資料整合，直接使用
        callback(bank_records);
        totalRow.value = total;
        bankRecordsList.value = bank_records;
        allExpandedItems.value = bank_records; // 用於匯出
      })
      .catch((err) => {
        console.log(err);
        toast({
          component: ToastificationContent,
          props: {
            title: "獲取銀行進支記錄列表時出錯",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const paymentMethodOptions = ref([]);

  const refetchOption = () => {
    store
      .dispatch("app/fetchOptionList", {
        receive_methods: true,
      })
      .then((response) => {
        paymentMethodOptions.value = response.data.receive_methods.sort ((a, b) => a.sort_order - b.sort_order);
      });
  };

  // 匯出功能 - 更新為新的銀行進支記錄格式，包含日期分組優化
  const exportRecords = (exportType = 'all') => {
    return new Promise((resolve, reject) => {
      let dataToExport = [];

      if (exportType === 'current') {
        // 匯出當前頁面數據
        dataToExport = bankRecordsList.value;
        processExportData(dataToExport, resolve);
      } else {
        // 匯出全部數據 - 需要重新獲取所有記錄
        store
          .dispatch("bankRecords/fetchBankRecords", {
            q: searchQuery.value,
            perPage: 999999, // 獲取所有記錄
            page: 1,
            sortBy: sortBy.value,
            sortDesc: isSortDirDesc.value,
            payment_method_id: filter_payment_method_id.value,
            start_date: filter_start_date.value,
            end_date: filter_end_date.value,
            min_amount: filter_min_amount.value,
            max_amount: filter_max_amount.value,
            is_savings: filter_is_savings.value,
            transaction_type: filter_transaction_type.value,
          })
          .then((response) => {
            const { bank_records } = response.data;
            processExportData(bank_records, resolve);
          })
          .catch((error) => {
            console.error('獲取全部記錄失敗:', error);
            reject(error);
          });
      }
    });
  };

  // 處理匯出數據的共用函數
  const processExportData = (dataToExport, resolve) => {
    // 按日期排序數據
    const sortedData = [...dataToExport].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB; // 升序排列
    });

    // 準備匯出數據 - 實現日期分組顯示
    const exportData = [];
    let lastDate = null;

    sortedData.forEach((item, index) => {
      const currentDate = item.date ? formatDate(item.date) : '-';

      // 如果日期改變且不是第一筆記錄，插入空白行作為分隔
      if (lastDate && lastDate !== currentDate && index > 0) {
        exportData.push({
          '日期': '',
          '進支詳情': '',
          '進支方式': '',
          '存入': '',
          '支出': '',
          '儲蓄/往來': '',
          '備注': ''
        });
      }

      // 處理進支方式，確保支票號碼不換行
      let paymentMethod = item.payment_method || '-';
      if (paymentMethod.includes('\n')) {
        // 將換行符替換為空格，確保在同一行顯示
        paymentMethod = paymentMethod.replace(/\n/g, ' ');
      }

      exportData.push({
        '日期': (lastDate !== currentDate) ? currentDate : '', // 相同日期組別內只顯示第一筆的日期
        '進支詳情': item.transaction_detail || '-',
        '進支方式': paymentMethod,
        '存入': item.deposit_amount > 0 ? Number(item.deposit_amount).toFixed(2) : '',
        '支出': item.withdrawal_amount > 0 ? Number(item.withdrawal_amount).toFixed(2) : '',
        '儲蓄/往來': item.is_savings ? '儲蓄' : '往來',
        '備注': item.remark || ''
      });

      lastDate = currentDate;
    });

    resolve(exportData);
  };

  // 格式化日期
  const formatDate = (date) => {
    if (!date) return '-';
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '-';

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  // 移除不再需要的函數，後端已統一處理

  onMounted(() => {
    refetchData();
    refetchOption();
  });

  return {
    fetchBankRecords,
    tableColumns,
    perPage,
    currentPage,
    totalRow,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    bankRecordsList,
    refBankRecordsListTable,
    ability,
    refetchData,
    filter_payment_method_id,
    filter_start_date,
    filter_end_date,
    filter_min_amount,
    filter_max_amount,
    filter_is_savings,
    filter_transaction_type,
    paymentMethodOptions,
    exportRecords,
    allExpandedItems,
  };
}
